import { comissionPercentage } from "../../../../../../../../app/utils/comissionPercentage";
import { formatMoneyValue } from "../../../../../../../../app/utils/formatMoneyValue";
import { formatName } from "../../../../../../../../app/utils/formatName";
import { ReactNode, SyntheticEvent } from "react";
import { TransactionByIdResponse } from "../../../../../../../../app/services/transactionService/transactionById";
import Status from "../../../../../../../components/Status";
import { cn } from "../../../../../../../../app/utils/cn";
import { formatDateAndTime } from "../../../../../../../../app/utils/formatDate";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { capitalizeFirstLetter } from "@/app/utils/capitalizeFirstLetter";
import { StatusEnum } from "@/app/utils/status";
import { useMutation } from "@tanstack/react-query";
import { handleSupport } from "@/app/services/transactionService/handleSupport";
import { CommissionSemaphore } from '@/view/components/CommissionSemaphore'

interface ItemProps {
  label: string;
  content: ReactNode;
  type?: "default" | "row";
  labelClassName?: string;
  contentClassName?: string;
}
const Item = ({
  label,
  content,
  type = "default",
  labelClassName,
  contentClassName,
}: ItemProps) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2",
        type === "row" &&
        "-m-1 flex-col items-start rounded-lg border border-primary/50 bg-primary/5 p-2",
      )}
    >
      <p
        className={cn(
          "whitespace-nowrap text-sm font-semibold text-black04",
          labelClassName,
        )}
      >
        {label}
      </p>
      {type === "default" && (
        <div className="h-1 w-full border-b-[1px] border-dotted border-[#CACACA]" />
      )}
      <div
        className={cn(
          "max-w-96 whitespace-nowrap text-xs font-semibold",
          contentClassName,
        )}
      >
        {content}
      </div>
    </div>
  );
};

type TrasanctionsDetailsInfoProps = {
  data: TransactionByIdResponse;
  isClient?: boolean;
};
export const TransactionsDetailsInfo = ({
  data,
  isClient = false,
}: TrasanctionsDetailsInfoProps) => {
  const { toast } = useToast();

  const copyLink = async (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const blob = new Blob([data.support?.link || data.url], {
      type: "text/plain",
    });
    const clipboardData = [new ClipboardItem({ "text/plain": blob })];
    await navigator.clipboard.write(clipboardData);

    toast({ variant: "default", title: "Link copiado" });

    if (
      !data?.support?.canUploadDocs &&
      data?.actualStatus === StatusEnum.ACTION_REQUIRED
    ) {
      void changeStatus(data._id);
    }
  };

  const { mutateAsync: changeStatus } = useMutation({
    mutationFn: async (id: string) => {
      if (!id) return;
      return handleSupport({ id, data: { isStatusLocked: false } });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erro ao alterar status da transação",
      });
    },
  });

  const { isAdmin, isSupport, isSeller } = useAuth();

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="py-5 font-semibold text-black03">Transação</p>
      </div>

      <div className="space-y-2 pb-5">
        <Item label="Contrato" content={data.contract} />
        {(data.config?.canCopyFormLink || data.support?.link) && (
          <Item
            label="Link de formalização"
            content={
              <Button
                onClick={copyLink}
                variant="link"
                className="h-auto p-0 text-info"
              >
                Copiar link
              </Button>
            }
          />
        )}
        <Item
          label="Produto"
          content={capitalizeFirstLetter(data.type || "Fgts")}
        />
        <Item
          label={isClient ? "Vendedor" : "Cliente"}
          content={formatName(isClient ? data.seller.name : data.customer.name)}
        />
        {!!data.value && (
          <Item
            label={
              data.tableName?.toLowerCase().includes("salvcar")
                ? "Proposta"
                : "Valor"
            }
            content={formatMoneyValue(data.value)}
          />
        )}
        {!!data?.commission && (
          <Item
            label="Comissão"
            content={
              <>
                {formatMoneyValue(data.commission)}{" "}
                {!data.tableName?.toLowerCase().includes("salvcar") &&
                  comissionPercentage(data.value, data.commission)}
              </>
            }
          />
        )}

        {(isAdmin || isSupport) &&
          Array.isArray(data.commissionList) &&
          data.commissionList.length > 0 &&
          [...data.commissionList]
            .sort((a, b) => {
              const order = { USER: 0, LEADER: 1, SUPERVISOR: 2 } as Record<
                string,
                number
              >;
              return (order[a.type] ?? 99) - (order[b.type] ?? 99);
            })
            .map((c, idx) => (
              <Item
                key={`${c.name}-${idx}`}
                label={`${c?.name?.split(" ")[0]}${c?.type !== "TRANSACTIONAL" ? " - Bônus" : ""}`}
                content={
                  <div className="flex items-center gap-1">
                    {formatMoneyValue(c.value)}{' '}
                    {data.tableName?.toLowerCase().includes('salvcar')
                      ? ''
                      : comissionPercentage(data.value, c.value)}{' '}
                    <CommissionSemaphore status={c.status} />
                  </div>
                }
                labelClassName="font-normal"
                contentClassName="font-normal"
              />
            ))}
        {data.tableName && <Item label="Tabela" content={data.tableName} />}
        {data.status && (
          <Item
            label="Status"
            content={<Status status={data.actualStatus} />}
          />
        )}
        {data.descriptionActivity && (
          <Item label="Atividade" content={data.descriptionActivity} />
        )}
        {data.updatedAt && (
          <Item
            label="Ultima atualização"
            content={formatDateAndTime(data.updatedAt)}
          />
        )}
        {data.createdAt && (
          <Item
            label="Data de lançamento"
            content={formatDateAndTime(data.createdAt)}
          />
        )}

        {data.reason_to_cancel && (
          <Item
            type="row"
            label="Motivo"
            content={
              <p className="w-full whitespace-break-spaces">
                {data.reason_to_cancel}
              </p>
            }
          />
        )}
        {data.ultimaObservacao && (
          <Item
            type="row"
            label="Última observação"
            content={
              <p className="w-full whitespace-break-spaces">
                {data.ultimaObservacao}
              </p>
            }
          />
        )}
      </div>

      {(isSeller ||
        (isSupport &&
          ![StatusEnum.PENDING, StatusEnum.ACTION_REQUIRED].includes(
            data.actualStatus,
          ))) &&
        data.support?.observation && (
          <div className="mb-2 flex flex-col gap-2">
            <p className="font-semibold text-black03">Suporte</p>
            <Item
              type="row"
              label="Observação"
              content={
                <p className="w-full whitespace-break-spaces">
                  {data.support?.observation}
                </p>
              }
            />
          </div>
        )}
    </div>
  );
};
